import React, { useState } from "react";
import { Rocket, Square, Eye, MousePointerClick, UserPlus, Megaphone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { countryFlag } from "@/components/admin/InstallsByCountryPanel";
import { countryNameIn } from "@/components/admin/CountryRelaunchDialog";
import { fmtInt, fmtDate } from "@/lib/activityFmt";

// Lot 767 — suivi des relances pays : inscriptions venues du pays depuis le lancement, bannière vue / cliquée, arrêt.
export const CountryRelaunchCard = ({ rows, onChange, canManage }) => {
  const [busy, setBusy] = useState(null);
  const act = async (r, fn, ok) => {
    setBusy(r.id);
    try { const { data } = await fn(); onChange?.(data); if (ok) toast.success(ok); }
    catch (e) { toast.error(e?.friendlyMessage || "Action impossible"); }
    setBusy(null);
  };
  const active = rows.filter((r) => r.status === "active");
  const stopped = rows.filter((r) => r.status !== "active").slice(0, 5);
  const Row = ({ r }) => (
    <li className="rounded-xl border p-3 text-sm" style={{ borderColor: "var(--yalola-border)", opacity: r.status === "active" ? 1 : 0.7 }} data-testid={`relaunch-row-${r.code}`} data-status={r.status}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xl leading-none">{countryFlag(r.code)}</span>
        <span className="font-semibold">{countryNameIn(r.code, "fr")}</span>
        <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{r.status === "active" ? `Relance du ${fmtDate(r.started_at)}` : `Du ${fmtDate(r.started_at)} au ${fmtDate(r.stopped_at)}`}</span>
        <span className="ml-auto inline-flex items-center gap-1 font-bold tabular-nums" style={{ color: r.signups_since > 0 ? "var(--yalola-ok)" : "inherit" }} data-testid={`relaunch-signups-${r.code}`}><UserPlus size={13} /> +{fmtInt(r.signups_since)} inscription(s)</span>
      </div>
      <div className="mt-2 flex items-center gap-3 flex-wrap text-xs" style={{ color: "var(--yalola-text-muted)" }}>
        <span className="inline-flex items-center gap-1" data-testid={`relaunch-seen-${r.code}`}><Eye size={12} /> {fmtInt(r.banner_seen)} vues</span>
        <span className="inline-flex items-center gap-1" data-testid={`relaunch-clicks-${r.code}`}><MousePointerClick size={12} /> {fmtInt(r.banner_clicks)} clics</span>
        <span className="inline-flex items-center gap-1">{fmtInt(r.installs_at_start)} install. au départ</span>
        {r.kit_enabled && <span className="inline-flex items-center gap-1"><Megaphone size={12} /> kit pub</span>}
        {r.status === "active" && canManage && (
          <>
            <label className="ml-auto inline-flex items-center gap-1.5">Bannière <Switch checked={!!r.banner_enabled} disabled={busy === r.id} onCheckedChange={(v) => act(r, () => api.put(`/admin/relaunch/${r.id}`, { banner_enabled: v }))} data-testid={`relaunch-banner-toggle-${r.code}`} /></label>
            <Button size="sm" variant="outline" className="rounded-full h-7 px-2.5" disabled={busy === r.id} onClick={() => window.confirm(`Arrêter la relance ${countryNameIn(r.code, "fr")} ?`) && act(r, () => api.post(`/admin/relaunch/${r.id}/stop`), "Relance arrêtée")} data-testid={`relaunch-stop-${r.code}`}>
              {busy === r.id ? <Loader2 size={12} className="animate-spin mr-1" /> : <Square size={12} className="mr-1" />} Arrêter
            </Button>
          </>
        )}
      </div>
    </li>
  );
  return (
    <div className="yalola-card p-4 min-w-0" data-testid="relaunch-card">
      <div className="flex items-center gap-2 mb-1"><Rocket size={16} style={{ color: "var(--yalola-primary)" }} /><div className="font-bold font-display">Relances pays</div><span className="chip text-[10px] ml-1" data-testid="relaunch-active-count">{active.length} en cours</span></div>
      <div className="text-xs mb-3" style={{ color: "var(--yalola-text-muted)" }}>Bannière de bienvenue aux visiteurs non connectés du pays + kit de pub ; inscriptions du pays comptées depuis le lancement.</div>
      {!rows.length ? <div className="text-sm py-4 text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="relaunch-empty">Aucune relance : utilise « Relancer » sur un pays installé sans inscription.</div> : (
        <ul className="space-y-2" data-testid="relaunch-list">
          {active.map((r) => <Row key={r.id} r={r} />)}
          {stopped.map((r) => <Row key={r.id} r={r} />)}
        </ul>
      )}
    </div>
  );
};
